import { NgClass } from '@angular/common';
import { Component, DestroyRef, effect, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { ProfileService } from '@oneComply/core/services/profile.service';
import { BootstrapIcon } from '@oneComply/shared/components/bootstrap-icon';
import { ADMIN_NAV_SECTIONS, AdminNavSection } from '../../data/admin.data';

const SIDEBAR_EXPAND_MS = 300;

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, NgClass, BootstrapIcon],
  templateUrl: './admin-sidebar.html',
})
export class AdminSidebar {
  private readonly destroyRef = inject(DestroyRef);
  private readonly profileService = inject(ProfileService);
  private footerDelayTimer: ReturnType<typeof setTimeout> | null = null;

  readonly isOpen = input(true);
  readonly closeSidebar = output<void>();

  protected readonly navSections = signal<AdminNavSection[]>(ADMIN_NAV_SECTIONS);
  protected readonly footerTextVisible = signal(this.isOpen());

  constructor() {
    if (typeof window !== 'undefined') {
      this.profileService
        .getMe()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            const isCompanyAdmin = response.data.role === 'admin';
            this.navSections.set(this.filterNavSections(isCompanyAdmin));
          },
          error: () => {
            this.navSections.set(this.filterNavSections(false));
          },
        });
    }

    let previousOpen = this.isOpen();

    effect(() => {
      const open = this.isOpen();
      const isDesktop =
        typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches;
      const opening = !previousOpen && open;

      if (this.footerDelayTimer) {
        clearTimeout(this.footerDelayTimer);
        this.footerDelayTimer = null;
      }

      if (!open) {
        this.footerTextVisible.set(false);
        previousOpen = open;
        return;
      }

      if (isDesktop && opening) {
        this.footerTextVisible.set(false);
        this.footerDelayTimer = setTimeout(
          () => this.footerTextVisible.set(true),
          SIDEBAR_EXPAND_MS,
        );
      } else {
        this.footerTextVisible.set(true);
      }

      previousOpen = open;
    });

    this.destroyRef.onDestroy(() => {
      if (this.footerDelayTimer) {
        clearTimeout(this.footerDelayTimer);
      }
    });
  }

  private filterNavSections(isCompanyAdmin: boolean): AdminNavSection[] {
    return ADMIN_NAV_SECTIONS.map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.companyAdminOnly || isCompanyAdmin,
      ),
    })).filter((section) => section.items.length > 0);
  }

  protected handleCloseSidebar(): void {
    this.closeSidebar.emit();
  }

  protected routerLinkActiveOptions(path: string): { exact: boolean } {
    return { exact: path === '/dashboard' };
  }

  protected ariaHidden(): string | null {
    if (this.isOpen()) {
      return null;
    }

    if (typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px)').matches) {
      return null;
    }

    return 'true';
  }
}

